import React from 'react';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import classNames from 'classnames';
import Navigation from 'src/components/Navigation';
import FontAwesomeIcon from 'src/components/Icon';
import SidebarHeader from 'src/layouts/WithNavigation/ModernSidebar/Components/SidebarHeader';
import SidebarFooter from 'src/layouts/WithNavigation/ModernSidebar/Components/SidebarFooter';
import useSidebarPermissions from 'src/layouts/WithNavigation/ModernSidebar/Hooks/useSidebarPermissions';
import {
  serviceProviderTopLinks,
  sharedBottomLinks,
} from 'src/layouts/WithNavigation/ModernSidebar/Constants';
import HomeOwnerSidebarNav from 'src/layouts/WithNavigation/ModernSidebar/CondtionalRoleBasedComponents/HomeOwnerSidebarNav';
import RealtorSidebarNav from 'src/layouts/WithNavigation/ModernSidebar/CondtionalRoleBasedComponents/RealtorSideBarNav';
import AdminSidebarNav from 'src/layouts/WithNavigation/ModernSidebar/CondtionalRoleBasedComponents/AdminSidebarNav';
import StrataManagerSidebarNav from 'src/layouts/WithNavigation/ModernSidebar/CondtionalRoleBasedComponents/StrataManagerSidebarNav';
import pkg from '../../../../package.json';
import './ModernSidebar.scss';

interface ModernSidebarProps {
  collapsableSideBar: () => void;
  collapseSideBar: boolean;
  from?: any;
  serviceRequest?: any;
  propertyDisabled?: boolean;
}

interface StaticRoleLinks {
  topLinks: any[];
  bottomLinks: any[];
}

const staticRoleLinks: Record<string, StaticRoleLinks> = {
  'Service Provider': {
    topLinks: serviceProviderTopLinks,
    bottomLinks: sharedBottomLinks,
  },
  'Service Manager': {
    topLinks: serviceProviderTopLinks,
    bottomLinks: sharedBottomLinks,
  },
};

export interface SidebarPermissionsData {
  allowedTabList: any[];
  orders: any[];
  properties: any[];
  selectedUserSuite: any;
  keyContacts: any[];
}

interface ConditionalRoleNavProps {
  collapseSideBar: boolean;
  permissions: SidebarPermissionsData;
}

const conditionalRoleComponents: Record<
  string,
  React.FC<ConditionalRoleNavProps>
> = {
  'Home Owner': HomeOwnerSidebarNav,
  Occupant: HomeOwnerSidebarNav,
  Realtor: RealtorSidebarNav,
  Admin: AdminSidebarNav,
  'Super Admin': AdminSidebarNav,
  'Property Developer': AdminSidebarNav,
  'Strata Manager': StrataManagerSidebarNav,
};

interface SidebarNavProps {
  role: string;
  collapseSideBar: boolean;
  permissions: SidebarPermissionsData;
}

const SidebarNav = ({
  role,
  collapseSideBar,
  permissions,
}: SidebarNavProps) => {
  const staticLinks: StaticRoleLinks = staticRoleLinks[role];

  const ConditionalRoleNav: React.FC<ConditionalRoleNavProps> =
    conditionalRoleComponents[role] ?? null;

  if (staticLinks) {
    return (
      <>
        <Navigation
          links={staticLinks.topLinks}
          collapseSideBar={collapseSideBar}
        />
        <div className="sidebar-spacer" />
        <Navigation
          links={staticLinks.bottomLinks}
          collapseSideBar={collapseSideBar}
        />
      </>
    );
  }

  if (ConditionalRoleNav) {
    return (
      <ConditionalRoleNav
        collapseSideBar={collapseSideBar}
        permissions={permissions}
      />
    );
  }

  return null;
};

const ModernSidebar = ({
  collapsableSideBar,
  collapseSideBar,
  from,
  serviceRequest,
  propertyDisabled,
}: ModernSidebarProps) => {
  const user: any = useSelector(selectAuthenticatedUser);
  const role: string = user?.roles?.[0]?.role?.name;

  const permissions: any = useSidebarPermissions();

  return (
    <>
      <div
        className={classNames('collapsable-bar', {
          'collapsable-bar-collapsed': collapseSideBar,
        })}
      >
        <div
          onClick={() => collapsableSideBar()}
          className="collapsable-bar-icon"
        >
          <FontAwesomeIcon weight="solid" glyph="bars" />
        </div>
        <span className="sidebar-version">v{pkg.version}</span>
      </div>
      <div
        className={classNames('sidebar', {
          'sidebar-collapsed': collapseSideBar,
        })}
      >
        <div className="sidebar-top">
          <SidebarHeader
            collapseSideBar={collapseSideBar}
            from={from}
            serviceRequest={serviceRequest}
            propertyDisabled={propertyDisabled}
            permissions={permissions}
          />
          <SidebarNav
            role={role}
            collapseSideBar={collapseSideBar}
            permissions={permissions}
          />
        </div>
        <SidebarFooter collapseSideBar={collapseSideBar} />
      </div>
    </>
  );
};

export default ModernSidebar;
